import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  FormStack,
  Input,
  ResponsiveGrid,
  Select,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { dayjs, toApiDate, type Dayjs } from "@marekl/adminer/datetime";
import { BrandLockup } from "../../../components/BrandLockup";
import { SupportContacts } from "../../../components/SupportContacts";
import { birthNumberProblem } from "../../../lib/birthNumber";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import {
  useRegisterTeam,
  usePublicSeason,
  type RegisterTeamPayload,
  type RegistrationFlow,
} from "../../../lib/queries/registration";
import { useOpenRegistrationFlows } from "../../../lib/queries/access";
import {
  ALL_CLOSED_MESSAGE,
  FLOW_CHOICES,
  closedReason,
  flowForPath,
  memberBlockedReason,
  upgradeNote,
} from "./flows";
import { PublicNotices } from "../../../components/Notices";
import styles from "./TeamRegistrationPage.module.css";

const required = (msg = "Povinné pole") =>
  z.string().trim().min(1, msg);

const today = dayjs();
const minBirth = today.subtract(120, "year");
const maxBirth = today.subtract(15, "year");

/**
 * The form asks what the flow needs, so the schema is built per flow
 * rather than validated by one superRefine afterwards: zod skips outer
 * refinements when the inner parse fails, which would hide „Zadejte rodné
 * číslo" behind any other empty field on the first submit.
 *
 * Mirrors registration.validate on the server, which makes the same split.
 */
function makeSchema(flow: RegistrationFlow) {
  const czech = flow !== "foreign";
  // A Czech club always gives the billing address; a foreign one gives it
  // only if the organizer will invoice startovné — and then all of it,
  // because the snapshot is immutable and half a row can never be
  // completed.
  const billed = (msg?: string) =>
    czech ? required(msg) : z.string().trim().optional().default("");

  return z
    .object({
      flow: z.enum(["cmf_member", "cup_only", "foreign"]),
      team: z.object({
        name: required("Zadejte název týmu"),
        // The state the club represents; a Czech club is Czech.
        country_code: czech
          ? z.string().trim().optional().default("")
          : required("Vyberte stát, který tým reprezentuje").length(
              2,
              "Vyberte stát, který tým reprezentuje",
            ),
      }),
      billing: z.object({
        contact_name: billed("Zadejte kontaktní osobu"),
        contact_email: czech
          ? z.string().trim().toLowerCase().pipe(z.string().email("Neplatný e-mail"))
          : z.string().trim().toLowerCase().optional().default(""),
        contact_phone: billed("Zadejte kontaktní telefon"),
        subject_name: z.string().trim().optional().default(""),
        street: billed(),
        city: billed(),
        postal_code: billed(),
        country_code: czech
          ? required().length(2, "Použijte dvoupísmenný ISO kód")
          : z.string().trim().optional().default(""),
        ico: z.string().trim().optional().default(""),
        dic: z.string().trim().optional().default(""),
      }),
      leader: z
        .object({
          first_name: required("Zadejte jméno"),
          last_name: required("Zadejte příjmení"),
          birth_date: z.custom<Dayjs | null>(
            (v) => v !== null && v !== undefined,
            { message: "Zadejte datum narození" },
          ),
          // The statute that demands a rodné číslo covers people reported
          // to the Rejstřík sportu as ČNSMS members, which a foreign club's
          // leader is not — and most have none to give.
          birth_number: czech
            ? required("Zadejte rodné číslo")
            : z.string().trim().optional().default(""),
          country_code: required().length(2, "Použijte dvoupísmenný ISO kód"),
          // The home address is part of the ČNSMS data set a season
          // registration demands.
          street: czech ? required() : z.string().trim().optional().default(""),
          city: czech ? required() : z.string().trim().optional().default(""),
          postal_code: czech ? required() : z.string().trim().optional().default(""),
          phone: z.string().trim().optional().default(""),
        })
        // An ERROR here, not the warning every other surface shows. This is
        // the one place the server REFUSES a rodné číslo (the founding
        // leader's, via LeaderBirthNumberError), because
        // ErrLeaderIdentityLocked freezes it the moment the team exists. A
        // refusal shown in help-text grey during typing and in red only
        // after submit is how somebody ends up filing a support ticket
        // about a form that looked fine.
        //
        // Empty is left to the required rule above; running both puts the
        // same sentence on the field twice.
        .superRefine((v, ctx) => {
          if (!v.birth_number.trim()) return;
          const problem = birthNumberProblem(
            v.birth_number,
            v.birth_date ? v.birth_date.format("YYYY-MM-DD") : undefined,
          );
          if (problem) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["birth_number"], message: problem });
          }
        }),
      account: z.object({
        email: z
          .string()
          .trim()
          .toLowerCase()
          .pipe(z.string().email("Neplatný e-mail")),
        password: z.string().min(8, "Heslo musí mít alespoň 8 znaků"),
        password_repeat: z.string(),
      }),
    })
    .refine((v) => v.account.password === v.account.password_repeat, {
      path: ["account", "password_repeat"],
      message: "Hesla se neshodují",
    });
}

// The Czech variant is the widest shape — every field the form can hold —
// so the form's types come from it whichever flow is being filled in.
type Schema = ReturnType<typeof makeSchema>;
type FormValues = z.input<Schema>;
type FormOutput = z.output<Schema>;

const defaultValues: FormValues = {
  flow: "cmf_member",
  team: { name: "", country_code: "" },
  billing: {
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    subject_name: "",
    street: "",
    city: "",
    postal_code: "",
    country_code: "CZ",
    ico: "",
    dic: "",
  },
  leader: {
    first_name: "",
    last_name: "",
    birth_date: null,
    birth_number: "",
    country_code: "CZ",
    street: "",
    city: "",
    postal_code: "",
    phone: "",
  },
  account: { email: "", password: "", password_repeat: "" },
};

export function TeamRegistrationPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mutateAsync: register, isPending } = useRegisterTeam();
  const { data: season } = usePublicSeason();
  // Which door, read from the URL: each flow asks for different things, so
  // each is a page of its own that can be linked, bookmarked and returned
  // to with the browser's own Back.
  const flow = flowForPath(pathname);
  // Doors an administrator has closed (service/access). Unknown while it
  // loads, and then nothing is closed: the server refuses a closed door
  // anyway, and a page that flashes „uzavřeno" at everybody is worse.
  const { data: openFlows } = useOpenRegistrationFlows();
  const isClosed = (f: RegistrationFlow) => !!openFlows && !openFlows.includes(f);

  const {
    register: rhfRegister,
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormValues, unknown, FormOutput>({
    // Rebuilt when the flow changes: which fields are required is baked
    // into the schema, not decided afterwards.
    resolver: useMemo(
      () => zodResolver(makeSchema(flow ?? "cmf_member")) as Resolver<FormValues, unknown, FormOutput>,
      [flow],
    ),
    defaultValues,
    mode: "onTouched",
  });

  // The form's own `flow` field follows the route, so a link straight to a
  // flow submits as that flow rather than as the default.
  //
  // The billing country follows it too. On the foreign flow the whole
  // section is optional, and a <select> that fills itself in with „Česká
  // republika" both states the wrong country and makes an untouched
  // section look half filled to the server (registration.hasBilling).
  useEffect(() => {
    if (flow) setValue("flow", flow);
    setValue("billing.country_code", flow === "foreign" ? "" : "CZ");
  }, [flow, setValue]);
  const isForeign = flow === "foreign";

  // The bot filter the server applies (registration.go): when the form was
  // opened, and a field nobody sees. A person needs far longer than the
  // server's minimum to fill this form in, and never types into the field.
  // performance.now() is monotonic: a clock set back while the form is
  // open cannot make the time negative and refuse a person.
  const openedAt = useRef(performance.now());
  const honeypot = useRef<HTMLInputElement>(null);

  async function onSubmit(values: FormOutput) {
    const payload: RegisterTeamPayload = {
      flow: values.flow,
      hp_check: honeypot.current?.value ?? "",
      form_ms: Math.round(performance.now() - openedAt.current),
      team: {
        name: values.team.name,
        // The Czech flows do not ask; the server fills CZ in either way.
        country_code: values.flow === "foreign" ? values.team.country_code : "CZ",
      },
      billing: values.billing,
      leader: {
        ...values.leader,
        birth_date: toApiDate(values.leader.birth_date) ?? "",
      },
      account: {
        email: values.account.email,
        password: values.account.password,
        display_name: `${values.leader.first_name} ${values.leader.last_name}`.trim(),
      },
    };
    try {
      await register(payload);
    } catch (err) {
      apiErrorToFormErrors<FormValues>(err, setError, {
        fallbackMessage: "Registrace selhala. Zkontrolujte prosím údaje.",
      });
    }
  }

  if (isSubmitSuccessful) {
    return (
      <div className={styles.page}>
        <div className={styles.notices}>
          <PublicNotices />
        </div>
        <Card className={styles.card}>
          <Card.Content>
            <div className={styles.header}>
              <BrandLockup />
              <p className={styles.subtitle}>Žádost odeslána</p>
            </div>
            <p>
              {flow === "foreign"
                ? "Děkujeme. Vaše žádost čeká na schválení administrátorem ČNSMS. Po schválení se přihlásíte zadaným e-mailem a heslem a budete se moci přihlašovat na pohárové soutěže. O schválení vám pošleme e-mail."
                : "Děkujeme. Vaše žádost čeká na schválení administrátorem ČNSMS. Po schválení se přihlásíte zadaným e-mailem a heslem. O schválení vám pošleme e-mail."}
            </p>
            <Button variant="primary" block onClick={() => navigate("/login")}>
              Přejít na přihlášení
            </Button>
          </Card.Content>
        </Card>
        <SupportContacts />
      </div>
    );
  }

  const rootMessage = rootErrorMessage(errors);

  if (flow === null) {
    // Both sentences come from the same season read, so the two tiles can
    // never disagree about whether this season is still joinable.
    const listed = FLOW_CHOICES.filter((c) => !c.unlisted);
    const allClosed = listed.every((c) => isClosed(c.value));
    const closedTitles = listed.filter((c) => isClosed(c.value)).map((c) => c.title);
    // A closed door says so first; the season window only speaks for a
    // ČNSMS door that is otherwise open, since it names a date a closed door
    // does not have.
    const windowReason = isClosed("cmf_member") ? null : memberBlockedReason(season);
    const blockedReason =
      [closedTitles.length ? closedReason(closedTitles) : null, windowReason]
        .filter(Boolean)
        .join(" ") || null;
    const cupNote = upgradeNote(season);
    return (
      <div className={styles.page}>
        <div className={styles.notices}>
          <PublicNotices />
        </div>
        <Card className={styles.card}>
          <Card.Content>
            <div className={styles.header}>
              <BrandLockup />
              <p className={styles.subtitle}>Registrace nového týmu</p>
            </div>
            {allClosed ? (
              <Alert variant="info">{ALL_CLOSED_MESSAGE}</Alert>
            ) : (
              <>
                <p className={styles.choiceHint}>Vyberte, jak chcete tým registrovat.</p>
                {/* Side by side, so the two are read as alternatives rather
                    than as a list with a first item; each tile stacks its own
                    title over its sentence, and they collapse to one column on
                    a phone. */}
                <ResponsiveGrid columns={2}>
                  {listed.map((c) => {
                    // Full membership means registering a soupiska into the
                    // season, which runs in a window. Outside it the tile is
                    // declined with the date rather than leading a club into a
                    // form whose second half it cannot finish.
                    const blocked = isClosed(c.value) || (c.value === "cmf_member" && !!windowReason);
                    const note = c.value === "cup_only" ? cupNote : null;
                    return (
                      <Button
                        key={c.value}
                        block
                        // Most clubs registering here are joining ČNSMS, so that
                        // tile carries the brand and the other stands beside it
                        // as the alternative it is. The colour ranks them, it
                        // does not gate anything; the window does.
                        variant={c.value === "cmf_member" ? "primary" : "neutral"}
                        // Filled against outlined: the pair reads as „this one,
                        // or else that one" rather than as two equal blocks.
                        weight={c.value === "cmf_member" ? "soft" : "outline"}
                        className={styles.choice}
                        disabled={blocked}
                        onClick={() => navigate(c.path)}
                      >
                        <span className={styles.choiceTitle}>{c.title}</span>
                        <span className={styles.choiceDescription}>{c.description}</span>
                        {note && <span className={styles.choiceNote}>{note}</span>}
                      </Button>
                    );
                  })}
                </ResponsiveGrid>
                {/* Beside the disabled tile, never inside it: a screen reader
                    skips the content of a disabled control, and the reason is
                    the part somebody needs most. */}
                {blockedReason && (
                  <Alert variant="info" className={styles.choiceBlocked}>
                    {blockedReason}
                  </Alert>
                )}
              </>
            )}
            <p className={`${styles.footer} ${styles.choiceFooter}`}>
              Už máte účet? <Link to="/login">Přihlásit se</Link>
            </p>
          </Card.Content>
        </Card>
        <SupportContacts />
      </div>
    );
  }

  if (isClosed(flow)) {
    // The door is linkable, so somebody can arrive at a closed one
    // straight from a federation's e-mail.
    const title = FLOW_CHOICES.find((c) => c.value === flow)?.title ?? "";
    return (
      <div className={styles.page}>
        <div className={styles.notices}>
          <PublicNotices />
        </div>
        <Card className={styles.card}>
          <Card.Content>
            <div className={styles.header}>
              <BrandLockup />
              <p className={styles.subtitle}>{title}</p>
            </div>
            <Alert variant="info">{closedReason([title])}</Alert>
            <p className={`${styles.footer} ${styles.choiceFooter}`}>
              <Link to="/registrace-tymu">Jiný typ registrace</Link> ·{" "}
              <Link to="/login">Přihlášení</Link>
            </p>
          </Card.Content>
        </Card>
        <SupportContacts />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.notices}>
        <PublicNotices />
      </div>
      <Card className={styles.card}>
        <Card.Content>
          <div className={styles.header}>
            <BrandLockup />
            <p className={styles.subtitle}>
              {FLOW_CHOICES.find((c) => c.value === flow)?.title}
            </p>
            <Button weight="ghost" size="sm" onClick={() => navigate("/registrace-tymu")}>
              Změnit typ registrace
            </Button>
          </div>

          <p className={styles.requiredHint}>
            Pole označená <span className={styles.requiredMark}>*</span> jsou povinná.
          </p>

          <FormStack as="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {rootMessage && <Alert variant="err">{rootMessage}</Alert>}

            <div className={styles.honeypot} aria-hidden="true">
              {/* eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- a honeypot: deliberately invisible and never for a person to fill in, so it must not be a labelled lib control (AGENTS §5, exception 2) */}
              <input ref={honeypot} type="text" name="hp_check" tabIndex={-1} autoComplete="off" defaultValue="" />
            </div>

            <section>
              <h2 className={styles.sectionTitle}>Tým</h2>
              <FormStack>
                <Input
                  label="Název týmu"
                  required
                  error={errors.team?.name?.message}
                  {...rhfRegister("team.name")}
                />
                {/* The state the club represents, which is what a startovní
                    listina shows beside its name. Asked only here: a Czech
                    club is Czech. */}
                {isForeign && (
                  <Select
                    label="Stát, který tým reprezentuje"
                    required
                    error={errors.team?.country_code?.message}
                    {...rhfRegister("team.country_code")}
                  >
                    <option value="">Vyberte stát</option>
                    {COUNTRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                )}
              </FormStack>
            </section>

            <section>
              <h2 className={styles.sectionTitle}>
                Fakturační údaje{isForeign ? " (nepovinné)" : ""}
              </h2>
              <p className={styles.sectionHint}>
                {isForeign
                  ? "Vyplňte jen tehdy, pokud vám má pořadatel fakturovat startovné. Pak je potřeba celá adresa; částečnou uložit nelze."
                  : "Slouží k fakturaci. Jako kontaktní osobu uveďte člověka, který bude řešit faktury a platby."}
              </p>
              <FormStack>
                <ResponsiveGrid columns={2}>
                  <Input
                    label="Kontaktní osoba / statutární zástupce"
                    required={!isForeign}
                    error={errors.billing?.contact_name?.message}
                    {...rhfRegister("billing.contact_name")}
                  />
                  <Input
                    label="Název subjektu (pokud jiný)"
                    error={errors.billing?.subject_name?.message}
                    {...rhfRegister("billing.subject_name")}
                  />
                </ResponsiveGrid>
                <ResponsiveGrid columns={2}>
                  <Input
                    label="E-mail pro fakturaci"
                    type="email"
                    required={!isForeign}
                    error={errors.billing?.contact_email?.message}
                    {...rhfRegister("billing.contact_email")}
                  />
                  <Input
                    label="Telefon pro fakturaci"
                    required={!isForeign}
                    error={errors.billing?.contact_phone?.message}
                    {...rhfRegister("billing.contact_phone")}
                  />
                </ResponsiveGrid>
                <Input
                  label="Ulice a číslo popisné"
                  required={!isForeign}
                  error={errors.billing?.street?.message}
                  {...rhfRegister("billing.street")}
                />
                <ResponsiveGrid columns={3}>
                  <Input
                    label="Město"
                    required={!isForeign}
                    error={errors.billing?.city?.message}
                    {...rhfRegister("billing.city")}
                  />
                  <Input
                    label="PSČ"
                    required={!isForeign}
                    error={errors.billing?.postal_code?.message}
                    {...rhfRegister("billing.postal_code")}
                  />
                  <Select
                    label="Stát"
                    required={!isForeign}
                    error={errors.billing?.country_code?.message}
                    {...rhfRegister("billing.country_code")}
                  >
                    {isForeign && <option value="">Vyberte stát</option>}
                    {COUNTRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </ResponsiveGrid>
                <ResponsiveGrid columns={2}>
                  <Input
                    label="IČO"
                    error={errors.billing?.ico?.message}
                    {...rhfRegister("billing.ico")}
                  />
                  <Input
                    label="DIČ"
                    error={errors.billing?.dic?.message}
                    {...rhfRegister("billing.dic")}
                  />
                </ResponsiveGrid>
              </FormStack>
            </section>

            <section>
              <h2 className={styles.sectionTitle}>Vedoucí</h2>
              <p className={styles.sectionHint}>
                Osoba, která tým registruje a bude do systému přihlášena jako
                vedoucí.
              </p>
              <FormStack>
                <ResponsiveGrid columns={2}>
                  <Input
                    label="Jméno"
                    required
                    error={errors.leader?.first_name?.message}
                    {...rhfRegister("leader.first_name")}
                  />
                  <Input
                    label="Příjmení"
                    required
                    error={errors.leader?.last_name?.message}
                    {...rhfRegister("leader.last_name")}
                  />
                </ResponsiveGrid>
                <ResponsiveGrid columns={3}>
                  <Controller
                    control={control}
                    name="leader.birth_date"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        label="Datum narození"
                        required
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        minDate={minBirth}
                        maxDate={maxBirth}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                  {/* The statute that demands a rodné číslo covers people
                      reported to the Rejstřík sportu as ČNSMS members; a
                      foreign club's leader is not one, and most have none
                      to give. See docs/rodne-cislo.md. */}
                  {!isForeign && (
                    <Input
                      label="Rodné číslo"
                      required
                      error={errors.leader?.birth_number?.message}
                      {...rhfRegister("leader.birth_number")}
                    />
                  )}
                  <Select
                    label="Státní příslušnost"
                    required
                    error={errors.leader?.country_code?.message}
                    {...rhfRegister("leader.country_code")}
                  >
                    {COUNTRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </ResponsiveGrid>
                {/* The home address is part of the ČNSMS data set a season
                    registration demands. A foreign club's leader registers
                    into no ČNSMS season, so only the phone is asked for. */}
                {isForeign ? (
                  <Input
                    label="Telefon"
                    error={errors.leader?.phone?.message}
                    {...rhfRegister("leader.phone")}
                  />
                ) : (
                  <>
                    <Input
                      label="Ulice a číslo popisné"
                      required
                      error={errors.leader?.street?.message}
                      {...rhfRegister("leader.street")}
                    />
                    <ResponsiveGrid columns={3}>
                      <Input
                        label="Město"
                        required
                        error={errors.leader?.city?.message}
                        {...rhfRegister("leader.city")}
                      />
                      <Input
                        label="PSČ"
                        required
                        error={errors.leader?.postal_code?.message}
                        {...rhfRegister("leader.postal_code")}
                      />
                      <Input
                        label="Telefon"
                        error={errors.leader?.phone?.message}
                        {...rhfRegister("leader.phone")}
                      />
                    </ResponsiveGrid>
                  </>
                )}
              </FormStack>
            </section>

            <section>
              <h2 className={styles.sectionTitle}>Přihlašovací údaje</h2>
              <p className={styles.sectionHint}>
                E-mail bude sloužit jako přihlašovací jméno. Po schválení
                registrace administrátorem ČNSMS získáte plný přístup do systému.
              </p>
              <FormStack>
                <Input
                  label="E-mail"
                  type="email"
                  required
                  autoComplete="email"
                  error={errors.account?.email?.message}
                  {...rhfRegister("account.email")}
                />
                <ResponsiveGrid columns={2}>
                  <Input
                    label="Heslo"
                    type="password"
                    required
                    autoComplete="new-password"
                    error={errors.account?.password?.message}
                    {...rhfRegister("account.password")}
                  />
                  <Input
                    label="Heslo znovu"
                    type="password"
                    required
                    autoComplete="new-password"
                    error={errors.account?.password_repeat?.message}
                    {...rhfRegister("account.password_repeat")}
                  />
                </ResponsiveGrid>
              </FormStack>
            </section>

            <Button type="submit" variant="primary" block loading={isPending}>
              Odeslat registraci
            </Button>

            <p className={styles.footer}>
              Už máte účet? <Link to="/login">Přihlásit se</Link>
            </p>
          </FormStack>
        </Card.Content>
      </Card>
      <SupportContacts />
    </div>
  );
}
